import { AxleSelectField } from '@/components/formFields/AxleSelectField';
import { VerticalStack } from '@/components/utils/linearLayouts';
import {
    EditCancelButtons,
    patientFieldStyles,
} from '@/pages/PatientDetailsPage/PatientDetailsPage.common';
import { FieldDisplay } from '@/pages/PatientDetailsPage/PatientProfileTab/PatientProfileTab.common';
import { PreferencesSectionLayout } from '@/pages/PatientDetailsPage/PatientVisitPreferences/PreferencesSectionLayout';
import { errorToast, successToast } from '@/toast';
import { renderLanguage } from '@/types/language';
import { renderSex } from '@/types/sex';
import { useInputState } from '@/utils/inputState';
import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { CircularProgress } from '@mui/material';
import { Language, Sex } from 'gql-types';
import React, { useCallback, useMemo, useState } from 'react';

const commonFieldProps = {
    _extraInputProps: {
        sx: patientFieldStyles,
    },
    _extraSelectStyles: patientFieldStyles,
    inlineLabel: true,
};

const ORDERED_PREFERRED_CLINICIAN_SEX_OPTIONS = [Sex.UNKNOWN].concat(
    Object.values(Sex).filter((sex) => sex !== Sex.UNKNOWN),
);

const EDIT_CLINICIAN_PREFERENCES_MUTATION = gql`
    mutation PatientPreferencesTabEditPatientClinicianPreferences(
        $patientId: UUID!
        $preferredClinicianSex: Sex
        $preferredLanguage: Language
    ) {
        editPatientClinicianPreferences(
            input: {
                patientId: $patientId
                preferredLanguage: $preferredLanguage
                preferredClinicianSex: $preferredClinicianSex
            }
        ) {
            id
            preferredClinicianSex
            preferredLanguage
        }
    }
`;

interface EditablePatientClinicianPreferencesProps {
    patientId: string;
    preferredLanguage: Language;
    preferredClinicianSex: Sex | null;
    editable?: boolean;
    onSave?: () => void;
}

export const EditablePatientClinicianPreferences = ({
    editable = false,
    onSave,
    patientId,
    preferredLanguage,
    preferredClinicianSex,
}: EditablePatientClinicianPreferencesProps) => {
    const languageState = useInputState<Language>(preferredLanguage, {
        required: true,
    });
    const preferredClinicianSexState = useInputState<Sex>(
        preferredClinicianSex ? preferredClinicianSex : Sex.UNKNOWN,
    );
    const [editClinicianPreferences] = useMutation(EDIT_CLINICIAN_PREFERENCES_MUTATION, {
        onCompleted: () => {
            successToast('Clinician Preferences Updated');
            onSave?.();
        },
    });
    const [isEditing, setIsEditing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const canSave = useMemo(
        () =>
            !isSubmitting &&
            [languageState.isDifferent, preferredClinicianSexState.isDifferent].some((a) =>
                Boolean(a),
            ),
        [isSubmitting, languageState.isDifferent, preferredClinicianSexState.isDifferent],
    );

    const resetAllFields = useCallback(() => {
        [languageState, preferredClinicianSexState].forEach((input) => input.reset());
    }, [languageState, preferredClinicianSexState]);

    const handleCancel = useCallback(() => {
        setIsEditing(false);
        resetAllFields();
    }, [setIsEditing, resetAllFields]);

    const handleSave = useCallback(() => {
        if (!canSave) {
            return;
        }
        setIsSubmitting(true);
        editClinicianPreferences({
            variables: {
                patientId,
                preferredLanguage: languageState.isDifferent ? languageState.value : undefined,
                preferredClinicianSex: preferredClinicianSexState.isDifferent
                    ? preferredClinicianSexState.value !== Sex.UNKNOWN
                        ? preferredClinicianSexState.value
                        : null
                    : undefined,
            },
        })
            .then(() => {
                setIsEditing(false);
            })
            .catch(() => {
                errorToast('There was an issue saving your changes');
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [
        canSave,
        languageState,
        editClinicianPreferences,
        preferredClinicianSexState,
        patientId,
        setIsSubmitting,
        setIsEditing,
    ]);
    return (
        <PreferencesSectionLayout
            title='Clinician Preferences'
            editButtons={
                editable ? (
                    <EditCancelButtons
                        isEditing={isEditing}
                        onCancel={handleCancel}
                        onSubmit={handleSave}
                        onEdit={() => setIsEditing(true)}
                        disableSave={!canSave}
                        disableCancel={isSubmitting}
                        saveLabel={
                            isSubmitting ? (
                                <CircularProgress size={10} color={'inherit'} />
                            ) : undefined
                        }
                    />
                ) : undefined
            }
        >
            {isEditing ? (
                <VerticalStack spacing={2}>
                    <AxleSelectField
                        enabled={!isSubmitting}
                        label={'Preferred Language'}
                        inputState={languageState}
                        options={Object.values(Language)}
                        optionToValue={(language) => language}
                        optionToLabel={(language) => renderLanguage(language)}
                        {...commonFieldProps}
                    />
                    <AxleSelectField
                        enabled={!isSubmitting}
                        label={'Preferred Sex'}
                        inputState={preferredClinicianSexState}
                        options={ORDERED_PREFERRED_CLINICIAN_SEX_OPTIONS}
                        optionToValue={(sex) => sex}
                        optionToLabel={(sex) =>
                            sex === Sex.UNKNOWN ? 'No Preference' : renderSex(sex)
                        }
                        {...commonFieldProps}
                    />
                </VerticalStack>
            ) : (
                <VerticalStack spacing={2}>
                    <FieldDisplay
                        label='Preferred Language'
                        value={renderLanguage(languageState.value)}
                    />
                    <FieldDisplay
                        label='Preferred Sex'
                        value={
                            preferredClinicianSexState.value === Sex.UNKNOWN
                                ? undefined
                                : renderSex(preferredClinicianSexState.value)
                        }
                    />
                </VerticalStack>
            )}
        </PreferencesSectionLayout>
    );
};
